import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { differenceInCalendarDays, differenceInHours, isBefore } from 'date-fns';

export interface OverdueContent {
  days: number;
  text: string;
  dueState: DueState;
}

export enum DueState {
  Soon = 'soon',
  Overdue = 'overdue',
  Future = 'future',
}
/*
 * Generate overdue text content
 * Takes a date and boolean for tooltip content and checks if the date is overdue.
 * Usage:
 *   value | overdue:true
 * Example:
 *   {{ Date | overdue:false }}
 *   formats to: (overdue)
 *   {{ Date | overdue:true }}
 *   formats to: 3 days overdue
 */
@Pipe({ name: 'overdue' })
export class OverduePipe implements PipeTransform {
  constructor(public translateService: TranslateService) {}
  transform(date: Date, toolTip: boolean): string {
    const content = this.getOverdueContent(date);
    let dueResult = '';
    if (content.dueState === DueState.Overdue) {
      dueResult = this.translateService.instant('COMMON.DUE.OVERDUE');
    } else if (content.dueState === DueState.Soon) {
      dueResult = this.translateService.instant('COMMON.DUE.DUE_SOON');
    }
    if (toolTip) {
      dueResult = content.text;
    }
    return dueResult;
  }

  /**
   * Calculate overdue days
   * @param dueDate Date object
   * @returns OverdueContent {days: 12, text:'12 days overdue', dueState: DueState}
   */
  getOverdueContent(dueDate: Date): OverdueContent {
    const today = new Date();
    let overdueText = '';
    let dueState: DueState;
    // Only calculate pure calendar day difference.
    const dueDays = Math.abs(differenceInCalendarDays(today, dueDate));

    if (isBefore(dueDate, today)) {
      if (dueDays === 0) {
        overdueText = this.translateService.instant('COMMON.DUE.HOURS_OVERDUE', {
          hours: Math.abs(differenceInHours(dueDate, today)),
        });
      } else {
        const overdueKey = dueDays !== 1 ? 'COMMON.DUE.DAYS_OVERDUE' : 'COMMON.DUE.DAY_OVERDUE';
        overdueText = this.translateService.instant(overdueKey, { days: dueDays });
      }
      dueState = DueState.Overdue;
    } else {
      //Not yet due.
      if (dueDays === 0) {
        overdueText = this.translateService.instant('COMMON.DUE.DUE_TODAY');
        dueState = DueState.Soon;
      } else if (dueDays === 1) {
        overdueText = this.translateService.instant('COMMON.DUE.DUE_TOMORROW');
        dueState = DueState.Soon;
      } else {
        overdueText = this.translateService.instant('COMMON.DUE.DUE_IN', { days: dueDays });
        dueState = DueState.Future;
      }
    }

    return { days: dueDays, text: overdueText, dueState: dueState };
  }
}
